/**
 * Sino do header, sempre visível em todas as telas, mesmo sem notificação.
 * Badge com as não lidas; o clique abre o painel com elas (popover não modal:
 * Esc devolve o foco ao sino; clique fora e Tab para fora fecham). Abrir marca
 * como lidas, por pessoa, no armazenamento local. Quando chega uma nova, o
 * sino balança de leve e ganha o badge: o aviso fica nele, não numa barra por
 * cima da página.
 */
import {
  FC,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Link} from 'react-router-dom';
import clsx from 'clsx';

import {useAuth} from 'app/modules/auth';
import {fromNow} from 'app/modules/tickets/ticketsView';

import {NOTIFICATION_KIND_ICON} from './headerNotifications';
import {
  readSeenNotifications,
  saveSeenNotifications,
} from './seenNotifications';
import {useHeaderNotifications} from './useHeaderNotifications';

const PANEL_ID = 'opx-notifications';
/** Duração do balanço do sino (a mesma da animação no CSS). */
export const RING_MS = 1100;

const badgeText = (count: number) => (count > 99 ? '99+' : String(count));

const bellLabel = (unread: number) =>
  unread === 0
    ? 'Notificações'
    : `Notificações: ${unread} não ${unread === 1 ? 'lida' : 'lidas'}`;

const NotificationBell: FC = () => {
  const userUid = useAuth().currentUser?.uid || '';
  const {items, status, retry, arrival, allTo} = useHeaderNotifications();

  const [seen, setSeen] = useState(() => readSeenNotifications(userUid));
  useEffect(() => setSeen(readSeenNotifications(userUid)), [userUid]);
  const unread = useMemo(
    () => items.filter(({id}) => !seen.has(id)),
    [items, seen]
  );

  const [open, setOpen] = useState(false);
  // Não lidas quando o painel abriu (ou que chegaram com ele aberto): ficam
  // marcadas no painel até ele fechar, mesmo já guardadas como lidas
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());
  const [ringing, setRinging] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Chegou notificação nova: o sino balança e o leitor de tela ouve
  useEffect(() => {
    if (!arrival) return undefined;
    setRinging(true);
    setAnnouncement(
      arrival.delta === 1
        ? 'Nova notificação'
        : `${arrival.delta} novas notificações`
    );
    const timer = window.setTimeout(() => setRinging(false), RING_MS);
    return () => window.clearTimeout(timer);
  }, [arrival]);

  // Painel aberto: o que está nele foi visto, inclusive o que chega com ele
  // aberto
  useEffect(() => {
    if (!open || !unread.length) return;
    setFresh(
      (previous) =>
        new Set([...Array.from(previous), ...unread.map(({id}) => id)])
    );
    setSeen(
      saveSeenNotifications(
        userUid,
        items.map(({id}) => id)
      )
    );
  }, [open, unread, items, userUid]);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    setFresh(new Set());
    if (returnFocus) triggerRef.current?.focus();
  };

  const focusables = () =>
    Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>('a[href], button') || []
    );

  // Ao abrir, o foco vai para o primeiro item (ou para o painel); clique fora
  // fecha
  useEffect(() => {
    if (!open) return undefined;
    (focusables()[0] || panelRef.current)?.focus();
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (
        target &&
        !triggerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        close(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onPanelKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    const list = focusables();
    if (!list.length) return;
    const index = list.indexOf(document.activeElement as HTMLElement);
    const move = (next: number) => {
      event.preventDefault();
      list[(next + list.length) % list.length]?.focus();
    };
    if (event.key === 'ArrowDown') move(index + 1);
    else if (event.key === 'ArrowUp') move(index < 0 ? -1 : index - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(list.length - 1);
  };

  // Tab para fora do painel fecha (o clique fora é o `mousedown` acima)
  const onPanelBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (
      next &&
      !panelRef.current?.contains(next) &&
      !triggerRef.current?.contains(next)
    ) {
      close(false);
    }
  };

  const count = unread.length;

  return (
    <div className='opx-appbar__notify'>
      <button
        ref={triggerRef}
        type='button'
        className={clsx(
          'opx-appbar__iconbtn opx-appbar__bell',
          ringing && 'is-ringing',
          open && 'is-open'
        )}
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-controls={open ? PANEL_ID : undefined}
        aria-label={bellLabel(count)}
        title={bellLabel(count)}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <i className='bi bi-bell' aria-hidden='true' />
        {count > 0 && (
          <span className='opx-appbar__badge' aria-hidden='true'>
            {badgeText(count)}
          </span>
        )}
      </button>
      {/* Só `aria-live`, sem `role=status`: o status da tela (o toast) segue
          sendo o único da página */}
      <span className='visually-hidden' aria-live='polite' aria-atomic='true'>
        {announcement}
      </span>

      {open && (
        <div
          ref={panelRef}
          id={PANEL_ID}
          className='opx-notif'
          role='dialog'
          aria-label='Notificações'
          tabIndex={-1}
          onKeyDown={onPanelKey}
          onBlur={onPanelBlur}
        >
          <div className='opx-notif__head'>Notificações</div>
          {status === 'loading' ? (
            <div className='opx-notif__loading' aria-busy='true'>
              {[0, 1, 2].map((line) => (
                <span key={line} className='opx-skel opx-notif__skel' />
              ))}
              <span className='visually-hidden'>Carregando</span>
            </div>
          ) : status === 'error' && !items.length ? (
            <p className='opx-notif__state'>
              Não deu para carregar.
              <button
                type='button'
                className='opx-notif__retry'
                onClick={retry}
              >
                Tentar de novo
              </button>
            </p>
          ) : !items.length ? (
            <p className='opx-notif__state'>Nenhuma notificação</p>
          ) : (
            <ul className='opx-notif__list'>
              {items.map((item) => {
                const isNew = fresh.has(item.id);
                return (
                  <li key={item.id}>
                    <Link
                      to={item.to}
                      className={clsx('opx-notif__item', isNew && 'is-unread')}
                      onClick={() => close(false)}
                    >
                      <i
                        className={clsx(
                          'bi opx-notif__icon',
                          NOTIFICATION_KIND_ICON[item.kind]
                        )}
                        aria-hidden='true'
                      />
                      <span className='opx-notif__main'>
                        <span className='opx-notif__title' title={item.title}>
                          {item.title}
                        </span>
                        <span className='opx-notif__meta'>
                          {item.context && (
                            <span className='opx-notif__context'>
                              {item.context}
                            </span>
                          )}
                          <span className='opx-notif__when'>
                            {fromNow(item.at)}
                          </span>
                        </span>
                      </span>
                      {isNew && (
                        <span className='opx-notif__dot'>
                          <span className='visually-hidden'>Não lida</span>
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          <div className='opx-notif__foot'>
            <Link
              to={allTo}
              className='opx-notif__all'
              onClick={() => close(false)}
            >
              Ver todos
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
